import { User } from '@/features/auth/AuthContext';
import { apiClient } from './client';

// Define API response types
export interface LoginResponse {
  token: string;
  user?: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
  };
  // These fields are returned by the actual API
  user_id?: number;
  email?: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface CompanyResponse {
  owner: number | null;
  legal_name: string;
  trade_name?: string;
  country: string | null;
  tax_id_number: string;
  tax_id_type: string | null;
  phone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  plan: string | null;
  is_active: boolean;
}

export interface EmailCodeSendRequest {
  email: string;
}

export interface EmailCodeVerifyRequest {
  email: string;
  verification_code: string;
}

export interface EmailCodeSendResponse {
  message: string;
}

export interface EmailCodeVerifyResponse {
  message: string;
}

// API endpoint paths
const API_PATHS = {
  login: '/users/login/',
  register: '/users/register/',
  company: '/users/company/',
  companyCreate: '/users/company/create/',
  sendEmailCode: '/users/send-verification-code/',
  verifyEmailCode: '/users/validate-verification-code/',
};

export const authApi = {
  /**
   * Fetch company information for the current user
   * @returns Promise with company response data
   */
  getCompany: async (): Promise<CompanyResponse> => {
    try {
      return await apiClient.get<CompanyResponse>(API_PATHS.company);
    } catch (error) {
      console.error('API get company error:', error);
      throw error;
    }
  },

  /**
   * Send login request to the API
   * @param email User email (will be sent as username)
   * @param password User password
   * @returns Promise with login response data
   */
  login: async (email: string, password: string): Promise<LoginResponse> => {
    try {
      console.log(`Sending login request to ${API_PATHS.login} with:`, { email });
      
      const data = await apiClient.postPublic<LoginResponse>(API_PATHS.login, {
        email,
        password
      });
      
      console.log('API Login Response:', data);

      // Handle the alternative API response format
      if (data.token && !data.user && data.email && data.user_id) {
        // Convert the login response to our expected format
        return {
          token: data.token,
          user: {
            id: data.user_id,
            email: data.email,
            username: data.email,
            first_name: '',
            last_name: ''
          }
        };
      }
      
      // Check if the response has the expected structure
      if (!data.user && !data.token) {
        console.error('Unexpected API response format:', data);
        throw new Error('Formato de respuesta no válido');
      }

      return data;
    } catch (error) {
      console.error('API login error:', error);
      throw error;
    }
  },

  /**
   * Save company information
   * @param companyData Company data to save
   * @returns Promise with company response data
   */
  saveCompany: async (companyData: Omit<CompanyResponse, 'owner' | 'plan' | 'is_active'>): Promise<CompanyResponse> => {
    try {
      // Remove email if present (should not be sent)
      const { email, ...dataToSend } = companyData as any;
      return await apiClient.post<CompanyResponse>(API_PATHS.companyCreate, dataToSend);
    } catch (error) {
      console.error('API save company error:', error);
      throw error;
    }
  },

  /**
   * Map API response user to application user format
   * @param apiUser User data from API response
   * @returns User object in application format
   */
  mapApiUserToUser: (apiUser: LoginResponse['user'] | undefined, apiEmail?: string, apiUserId?: number): User => {
    // Handle the case where the API returns user_id and email directly instead of a user object
    if (!apiUser && apiEmail && apiUserId) {
      return {
        email: apiEmail,
        id: apiUserId,
        username: apiEmail,
        firstName: '',
        lastName: '',
      };
    }
    
    if (!apiUser) {
      throw new Error('Usuario no encontrado en la respuesta');
    }
    
    return {
      email: apiUser.email,
      id: apiUser.id,
      firstName: apiUser.first_name || '',
      lastName: apiUser.last_name || '',
      username: apiUser.username,
    };
  },

  /**
   * Send email verification code
   * @param email User email
   * @returns Promise with email code send response
   */
  sendEmailCode: async (email: string): Promise<EmailCodeSendResponse> => {
    try {
      return await apiClient.postPublic<EmailCodeSendResponse>(API_PATHS.sendEmailCode, {
        email,
      });
    } catch (error) {
      console.error('API send email code error:', error);
      throw error;
    }
  },

  /**
   * Verify email code
   * @param email User email
   * @param verification_code Verification code
   * @returns Promise with email code verify response
   */
  verifyEmailCode: async (email: string, verification_code: string): Promise<EmailCodeVerifyResponse> => {
    try {
      return await apiClient.postPublic<EmailCodeVerifyResponse>(API_PATHS.verifyEmailCode, {
        email,
        verification_code,
      });
    } catch (error) {
      console.error('API verify email code error:', error);
      throw error;
    }
  },

  /**
   * Complete registration with verified email code
   * @param email User email
   * @param password User password
   * @param password2 Password confirmation
   * @param first_name User's first name
   * @param last_name User's last name
   * @param verification_code Verification code from email validation
   * @returns Promise with registration response data
   */
  completeRegistration: async (
    email: string, 
    password: string, 
    password2: string, 
    first_name: string, 
    last_name: string,
    verification_code: string
  ): Promise<LoginResponse> => {
    try {
      return await apiClient.postPublic<LoginResponse>(API_PATHS.register, {
        email,
        password,
        password2,
        first_name,
        last_name,
        verification_code,
      });
    } catch (error) {
      console.error('API complete registration error:', error);
      throw error;
    }
  }
};
